import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import usePaginatedCollectionState from 'Hooks/usePaginatedCollectionState';
import { CURRENT_TIMEFRAME, TIMEFRAME_QP } from 'Components/ComponentLibrary/Organisms/ScenarioChart';
import {
  getRowSetData,
  getTabColumns,
} from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Content/TabTemplatePicker/Allocations/TableView';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import { ROW_FILTER_QUERY_PARAM } from 'Views/Page/Content/Templates/WhatIfTemplate/Components/Sidebar/consts';
import { ALL_ROW_FILTER_OPTION } from 'Views/Page/Content/Modal/Layouts/ScenarioSelectionAssumptionsAndDetails/consts';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';

const ACTIONS = {
  INITIALIZE: 'initialize',
  SET_TABLE_TIMEFRAME: 'set-timeframe',
  SET_ROW_FILTER: 'set-row-filter',
  SET_EXPAND_ALL: 'set-expand-all',
};

const initialState = {
  tableTimeFrame: CURRENT_TIMEFRAME,
  rowFilter: ALL_ROW_FILTER_OPTION,
  isInitialized: false,
  expandAll: false,
};

const LOOKS_LIKE_YEAR = /\d{4}/;

function getRowFilterOptions(rowSets) {
  if (!rowSets) {
    return null;
  }

  const labelRows = rowSets.map(row => ({ id: row.label, prompt: row.label }));
  const allOption = { id: ALL_ROW_FILTER_OPTION, prompt: ALL_ROW_FILTER_OPTION };
  return [allOption, ...labelRows];
}

function init(state) {
  return {
    ...initialState,
    ...state,
  };
}

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.INITIALIZE:
      return { ...state, isInitialized: true };
    case ACTIONS.SET_TABLE_TIMEFRAME: {
      if (
        action.timeframe === CURRENT_TIMEFRAME
          || !LOOKS_LIKE_YEAR.test(state.tableTimeFrame)
      ) {
        return {
          ...state,
          tableTimeFrame: action.timeframe,
        };
      }

      return {
        ...state,
        tableTimeFrame: action.timeframe,
      };
    }
    case ACTIONS.SET_ROW_FILTER: {
      return {
        ...state,
        rowFilter: action.rowFilter,
      };
    }
    case ACTIONS.SET_EXPAND_ALL:
      return { ...state, expandAll: action.expandAll };
    default:
      throw new Error(`Unknown ScenarioDetailTableProvider action, ${action.type}`);
  }
};

const ScenarioDetailTableContext = React.createContext(undefined);
const ScenarioDetailTableProvider = ({
  children,
  relationship,
  resource,
}) => {
  const {
    pageContentCollection,
    pageContentResource,
  } = usePageContentContext();

  const [state, dispatch] = React.useReducer(reducer, initialState, init);

  if (!relationship && !resource) {
    throw new Error('ScenarioDetailTableProvider requires either a relationship or resource prop');
  }
  if (relationship && resource) {
    throw new Error('ScenarioDetailTableProvider requires either a relationship or resource prop, not both');
  }

  const relatedLink = relationship
    ? relationship.link('related').withQueryParams().equal(ROW_FILTER_QUERY_PARAM, state.rowFilter)
    : pageContentResource.link('self');

  const {
    loading,
    loadingError,
    collection,
    refreshCollection,
    setCollection,
    fetchCollectionByLink,
  } = usePaginatedCollectionState({
    link: relatedLink, // link to fetch collection data
    suppressError: true, // Handled by data container
  });

  useComponentDidMount(() => {
    let timeframe;
    if (relatedLink) {
      const relatedUrl = new URL(relatedLink.url());
      timeframe = relatedUrl.searchParams.get(TIMEFRAME_QP);
    } else {
      const pageContentUrl = new URL(pageContentCollection.link('self').attr('url'));
      timeframe = pageContentUrl.searchParams.get(TIMEFRAME_QP);
    }

    if (timeframe) {
      dispatch({ type: ACTIONS.SET_TABLE_TIMEFRAME, timeframe });
    }

    dispatch({ type: ACTIONS.INITIALIZE });
  });

  const setTableTimeFrame = React.useCallback((timeframe) => {
    dispatch({ type: ACTIONS.SET_TABLE_TIMEFRAME, timeframe });
  });

  const setRowFilter = React.useCallback((rowFilter) => {
    dispatch({ type: ACTIONS.SET_ROW_FILTER, rowFilter });
  });

  let tabResource = resource;

  // if we have a relationship, we need to get the tab resource from the relationship
  if (relationship) {
    const detailsAttrName = 'facetPlanScenarioDetails';
    const facetPlanScenarioDetailsAttr = tabResource?.attr(detailsAttrName);

    if (facetPlanScenarioDetailsAttr) {
      tabResource = facetPlanScenarioDetailsAttr?.resources()?.head();
    } else {
      // This gets used when we open the modal from a modal
      tabResource = collection?.resources()?.head()
        ?.attr(detailsAttrName)?.resources()
        ?.head();
    }
  }

  // Timeframe options
  const timeFrameOptions = tabResource
    ? tabResource?.meta()?.queries()?.head()?.field(TIMEFRAME_QP)
      ?.options()
      .list()
      .map(option => ({
        prompt: option.attr('prompt'),
        id: option.attr('id'),
      })) : [];

  const onTimeFrameChange = React.useCallback(async (newTimeFrame) => {
    setTableTimeFrame(newTimeFrame);

    return fetchCollectionByLink(relatedLink.withQueryParams().equal(TIMEFRAME_QP, newTimeFrame));
  }, [relatedLink, setTableTimeFrame, fetchCollectionByLink]);

  const onRowFilterChange = React.useCallback((newRowFilter) => {
    setRowFilter(newRowFilter);
  }, [setRowFilter]);

  // Get tab columns and row set data
  const columnLabels = getTabColumns(tabResource?.attr('columnLabels'));
  const rowSets = getRowSetData(tabResource?.attr('rowSets'));
  const rowFilterOptions = getRowFilterOptions(rowSets);
  const forceShowColumns = tabResource?.attr('forceShowColumns') === 'true';

  // Filter rows shown based on rowFilter
  let filteredRowSets = rowSets;
  if (state.rowFilter && state.rowFilter !== ALL_ROW_FILTER_OPTION) {
    filteredRowSets = rowSets.filter(row => row.label === state.rowFilter);
  }

  const setExpandAll = useCallback((expandAll) => {
    dispatch({ type: ACTIONS.SET_EXPAND_ALL, expandAll });
  }, [dispatch]);

  const memoizedValue = React.useMemo(() => {
    return {
      loading,
      loadingError,
      setExpandAll,
      expandAll: state.expandAll,
      scenarioDetailsCollection: collection,
      refreshScenarioDetailsCollection: refreshCollection,
      setScenarioDetailsCollection: setCollection,
      onTimeFrameChange,
      tableTimeFrame: state.tableTimeFrame,
      timeFrameOptions,
      columnLabels,
      rowSets: filteredRowSets,
      rowFilter: state.rowFilter,
      onRowFilterChange,
      rowFilterOptions,
      forceShowColumns,
    };
  }, [
    loading,
    loadingError,
    collection,
    filteredRowSets,
    forceShowColumns,
  ]);

  return (
    <ScenarioDetailTableContext.Provider value={memoizedValue}>
      {children}
    </ScenarioDetailTableContext.Provider>
  );
};

// Use ScenarioDetailTable State Context
const useScenarioDetailTableContext = () => {
  const context = React.useContext(ScenarioDetailTableContext);
  if (context === undefined) {
    throw new Error('useScenarioDetailTableContext must be used within a ScenarioDetailTableProvider');
  }
  return context;
};

ScenarioDetailTableProvider.defaultProps = {
  relationship: null,
  resource: null,
};

ScenarioDetailTableProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  relationship: PropTypes.object,
  resource: PropTypes.object,
};

export {
  ScenarioDetailTableProvider,
  useScenarioDetailTableContext,
};
